// src/features/product-tours/HelpTourAutoPrompt.tsx
// One-shot : propose le guide Aide si jamais vu (le détail vit dans le registre).

import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Typography, Space } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { isAppLocale } from '../../i18n';
import { useProductTour } from './ProductTourProvider';
import { hasSeenTour, writeLocalSeenTour } from './seenToursStorage';
import type { ProductTourId } from './types';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';

const { Title, Paragraph } = Typography;

const TOUR_ID: ProductTourId = 'help_guides_v1';
const BRAND = '#d946ef';
const INTRO_DELAY_MS = 4500;

function isBlockedPath(pathname: string): boolean {
  return (
    pathname.startsWith('/super-admin') ||
    pathname === '/login' ||
    pathname === '/business-selection'
  );
}

const HelpTourAutoPrompt: React.FC = () => {
  const { t } = useAppTranslation('common');
  const { user, userProfile, patchUserProfile } = useAuth();
  const { startTour, isTourRunning } = useProductTour();
  const location = useLocation();
  const [introOpen, setIntroOpen] = useState(false);
  /** Empêche une 2ᵉ ouverture (ex. navigation /aide pendant le guide). */
  const promptConsumedRef = useRef(false);

  const uid = user?.uid;
  const alreadySeen =
    !!uid && hasSeenTour(uid, TOUR_ID, userProfile?.seenTours);

  const markSeenOnly = async () => {
    if (!uid) return;
    writeLocalSeenTour(uid, TOUR_ID);
    const nextSeen = { ...(userProfile?.seenTours || {}), [TOUR_ID]: true };
    patchUserProfile({ seenTours: nextSeen });
    try {
      await updateDoc(doc(db, COLLECTIONS.users, uid), {
        [`seenTours.${TOUR_ID}`]: true,
      });
    } catch (error) {
      console.warn('Impossible d’enregistrer seenTours:', error);
    }
  };

  useEffect(() => {
    if (!uid || !userProfile || !isAppLocale(userProfile.preferredLocale) || alreadySeen) {
      setIntroOpen(false);
      return;
    }
    if (promptConsumedRef.current || isTourRunning()) {
      return;
    }
    if (isBlockedPath(location.pathname)) {
      return;
    }

    const timer = window.setTimeout(() => {
      // Re-check : le guide a pu démarrer pendant le délai (ou navigation).
      if (
        promptConsumedRef.current ||
        isTourRunning() ||
        hasSeenTour(uid, TOUR_ID, userProfile?.seenTours)
      ) {
        return;
      }
      promptConsumedRef.current = true;
      setIntroOpen(true);
    }, INTRO_DELAY_MS);

    return () => window.clearTimeout(timer);
    // Ne pas dépendre de isTourRunning (identité instable) — check au fire du timer.
    // location.pathname sert à bloquer SA/login ; promptConsumedRef empêche
    // de replanifier quand le guide navigue vers /aide.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isTourRunning lu au timeout
  }, [uid, userProfile, alreadySeen, location.pathname]);


  if (!uid || alreadySeen) return null;

  return (
    <Modal
      open={introOpen}
      closable={false}
      maskClosable={false}
      footer={null}
      centered
      width={420}
      styles={{ body: { padding: '28px 24px 20px' } }}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <div style={{ textAlign: 'center' }}>
          <QuestionCircleOutlined style={{ fontSize: 40, color: BRAND }} />
          <Title level={4} style={{ marginTop: 12, marginBottom: 8 }}>
            {t('tours.help.introTitle')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('tours.help.introLead')}
          </Paragraph>
        </div>
        <Button
          type="primary"
          block
          size="large"
          onClick={() => {
            promptConsumedRef.current = true;
            setIntroOpen(false);
            // Marquer tout de suite : sinon seenTours n’arrive qu’à la fin du guide
            // et un remount / changement de route peut rouvrir la modale.
            if (uid) {
              writeLocalSeenTour(uid, TOUR_ID);
            }
            window.setTimeout(() => startTour(TOUR_ID), 280);
          }}
          style={{ background: BRAND, borderColor: BRAND, fontWeight: 600 }}
        >
          {t('tours.help.introCta')}
        </Button>
        <Button
          type="link"
          block
          onClick={() => {
            promptConsumedRef.current = true;
            setIntroOpen(false);
            void markSeenOnly();
          }}
        >
          {t('tours.common.skip')}
        </Button>
      </Space>
    </Modal>
  );
};

export default HelpTourAutoPrompt;
